import { Link, Outlet } from 'react-router';
import { env } from '@/lib/env';
import { AuthDialogProvider } from '@/features/auth/AuthDialogProvider';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { BottomNav } from './BottomNav';
import { PageMotion } from '@/components/motion/PageMotion';
import { MotionToggle } from '@/components/motion/MotionToggle';

/**
 * The shell every public page renders inside.
 *
 * Navigation lives in a floating pill at the bottom rather than in the header —
 * on desktop as well as mobile. The header is left with what belongs at the top
 * of a page: who you are, and a way back to the start.
 */
export function RootLayout() {
  return (
    // Inside the router, because closing and succeeding both navigate — and
    // outside the Outlet, so every page can open it.
    <AuthDialogProvider>
      <Shell />
    </AuthDialogProvider>
  );
}

function Shell() {
  const { user, isAuthenticated, isLoading } = useAuth();
  // Inside the provider, which is why the header lives in Shell rather than in
  // RootLayout: useAuthDialog throws outside it by design.
  const { open } = useAuthDialog();

  return (
    <div className="site-shell flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand-500 focus:px-4 focus:py-2 focus:text-surface-950"
      >
        İçeriğe geç
      </a>

      <header className="site-header sticky top-0 z-30">
        <div className="site-container site-header-inner flex items-center justify-between gap-3">
          <Link to="/" className="header-wordmark shrink-0">
            {/*
              width and height are set even though the class controls the size:
              without them the header collapses and re-expands as the image
              arrives, and everything below it jumps.

              The alt text IS the site name — a logo that links home is not
              decoration, and "" here would leave the only route back to the
              start unnamed.
            */}
            <img
              src="/gee-logo.png"
              alt={env.appName}
              width={881}
              height={519}
              // The mark is above the fold on every page, so it is worth
              // fetching before anything else asks for bandwidth.
              fetchPriority="high"
              className="h-10 w-auto sm:h-12"
            />
            <span aria-hidden="true" className="header-tag">
              GEE FAMILY
              <br />
              YAYIN · TOPLULUK
            </span>
          </Link>

          {/* Nothing renders until the session probe settles, so the header does
              not flash "Kayıt ol" at somebody who is already signed in. */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            <MotionToggle />
            {isLoading ? (
              <span
                className="h-10 w-24 animate-pulse rounded-xl bg-surface-800/50"
                aria-label="Oturum yükleniyor"
              />
            ) : isAuthenticated && user ? (
              <Link
                to="/profile"
                className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-sm text-surface-300 transition hover:bg-surface-800 hover:text-surface-100"
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="size-8 rounded-full object-cover ring-1 ring-brand-500/40"
                    width={32}
                    height={32}
                  />
                ) : (
                  <span className="grid size-8 place-items-center rounded-full bg-surface-700 text-xs font-semibold">
                    {user.userName.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="max-w-28 truncate">{user.userName}</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => open('login')}
                  className="gf-button gf-button-ghost"
                >
                  Giriş yap
                </button>
                <button
                  type="button"
                  onClick={() => open('register')}
                  className="gf-button gf-button-primary"
                >
                  Kayıt ol
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/*
        The bottom padding clears the floating bar. Without it the last card on
        every page sits underneath it and cannot be tapped.
      */}
      <main id="main" className="site-container site-main flex-1">
        <PageMotion>
          <Outlet />
        </PageMotion>
      </main>

      <footer className="site-footer">
        <div className="site-container footer-inner">
          <div>
            <p className="footer-brand">
              GEE FAMILY<span className="text-brand-400">.</span>
            </p>
            <p className="mt-1 text-xs text-surface-500">
              © {new Date().getFullYear()} {env.appName}
            </p>
          </div>
          <div className="flex max-w-md items-center gap-3">
            <span className="age-mark" aria-hidden="true">
              18+
            </span>
            <p className="text-xs leading-relaxed text-surface-500">
              18+. Kumar bağımlılık yapabilir. Sorumlu oyun oynayın.
            </p>
          </div>
        </div>
      </footer>

      <BottomNav />
    </div>
  );
}
